import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import UserService from '../services/UserService';
import { User, UserRole } from '../services/models/user';

export interface LocationRole {
  user?: User;
  isLoading: boolean;
  // The viewer may manage this site: rename it, add/rename/(de)activate its
  // stations, delete it, and manage its team.
  canManage: boolean;
  // Rentals for THIS location (already ANDed with the owner's feature by the
  // API). Falls back to the legacy flat flag against an older API.
  rentalsEnabled: boolean;
  // The viewer only ever reaches locations someone else owns.
  isMemberOnly: boolean;
}

/**
 * Resolves what the current viewer may do on a single location, from the
 * already-cached `['user']` query. Pass a siteId explicitly when the component
 * is not mounted under `/site/:siteId` (or just let it read the route param).
 */
const useLocationRole = (siteIdOverride?: string): LocationRole => {
  const params = useParams<{ siteId?: string }>();
  const siteId = siteIdOverride ?? params.siteId;

  const { data: user, isLoading } = useQuery({
    queryKey: ['user'],
    queryFn: () => UserService.getUser(),
    select: (data) => data.data,
  });

  const access = user?.access;
  // Route params are strings, `locationId` is a number — compare as strings.
  const entry = siteId != null
    ? access?.locations.find((location) => String(location.locationId) === String(siteId))
    : undefined;

  const canManage = access
    ? access.kind === 'admin' || entry?.isOwner === true
    // Older API without the access block: fall back to the flat role.
    : user != null && [UserRole.ADMIN, UserRole.OWNER].includes(user.role);

  const rentalsEnabled = entry ? entry.rentalEnabled : (user?.rentalEnabled ?? false);

  return {
    user,
    isLoading,
    canManage,
    rentalsEnabled,
    isMemberOnly: access?.kind === 'member',
  };
};

export default useLocationRole;
